import React from "react";
import { Smartphone, Zap, ShieldCheck, Star } from "lucide-react";
import StoreBadges from "./StoreBadges";

const perks = [
  { icon: Zap, title: "Lightning-fast", desc: "60fps native apps built for real travel days." },
  { icon: ShieldCheck, title: "Private by default", desc: "Encrypted storage, transparent policies." },
  { icon: Smartphone, title: "iOS & Android", desc: "Native experience on every device." },
];

const GetTheApp = ({ onTrackDownload }) => {
  return (
    <section id="get-app" className="relative py-24 md:py-32 bg-[#efe6d4]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative rounded-[36px] bg-gradient-to-br from-emerald-800 via-emerald-900 to-emerald-950 overflow-hidden p-8 md:p-14 lg:p-16 grain">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-30" style={{
            backgroundImage: "radial-gradient(circle at 70% 40%, rgba(251,191,36,0.35), transparent 55%)"
          }} />

          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 mb-6">
                <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                <span className="text-white/90 text-xs uppercase tracking-[0.18em]">4.9 ★ App Store &amp; Play Store</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-[1.02]">
                The whole trip, in your <span className="italic text-amber-200">pocket.</span>
              </h2>
              <p className="mt-5 text-white/80 text-lg max-w-lg">
                Download Splitara for iOS or Android and turn your next group getaway from chaotic threads and shared docs into a single, calm plan.
              </p>

              <div className="mt-8">
                <StoreBadges size="lg" theme="light" onClickTrack={onTrackDownload} />
              </div>

              <div className="mt-10 grid sm:grid-cols-3 gap-4">
                {perks.map((p, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 grid place-items-center shrink-0">
                      <p.icon className="w-4 h-4 text-amber-200" />
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">{p.title}</div>
                      <div className="text-white/70 text-xs mt-0.5">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone mockups */}
            <div className="relative h-[520px] md:h-[600px] hidden lg:block">
              <PhoneMock className="absolute right-8 top-2 rotate-[6deg]" tone="light" />
              <PhoneMock className="absolute right-40 top-16 -rotate-[8deg]" tone="dark" />
            </div>

            <div className="lg:hidden relative h-[420px] mt-4">
              <PhoneMock className="absolute left-1/2 -translate-x-[70%] top-4 -rotate-[8deg]" tone="dark" />
              <PhoneMock className="absolute left-1/2 -translate-x-[30%] top-0 rotate-[6deg]" tone="light" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PhoneMock = ({ className = "", tone = "light" }) => {
  const isLight = tone === "light";
  return (
    <div className={`floaty ${className}`}>
      <div className="w-[240px] md:w-[260px] aspect-[9/19] rounded-[38px] bg-black p-2 shadow-2xl border border-white/10">
        <div className={`w-full h-full rounded-[30px] overflow-hidden ${isLight ? "bg-[#f6f1e7]" : "bg-emerald-950"} relative`}>
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-black" />
          <div className="p-5 pt-10">
            <div className={`text-xs uppercase tracking-widest ${isLight ? "text-emerald-900/60" : "text-emerald-300/70"}`}>Trip</div>
            <div className={`font-display text-2xl font-semibold ${isLight ? "text-emerald-950" : "text-white"}`}>Amalfi Coast</div>
            <div className={`mt-1 text-xs ${isLight ? "text-emerald-950/60" : "text-white/60"}`}>7 days • 4 travellers</div>

            <div className="mt-5 space-y-2">
              {[
                { t: "Positano lunch", s: "12:30", c: "amber" },
                { t: "Boat to Capri", s: "15:00", c: "sky" },
                { t: "Sunset in Ravello", s: "19:15", c: "rose" },
              ].map((it, i) => (
                <div key={i} className={`rounded-xl p-3 ${isLight ? "bg-white" : "bg-white/5"} border ${isLight ? "border-black/5" : "border-white/10"}`}>
                  <div className={`text-xs ${isLight ? "text-emerald-950/50" : "text-white/50"}`}>{it.s}</div>
                  <div className={`text-sm font-medium ${isLight ? "text-emerald-950" : "text-white"}`}>{it.t}</div>
                </div>
              ))}
            </div>

            <div className={`mt-5 rounded-xl p-3 ${isLight ? "bg-emerald-900" : "bg-amber-200/90"}`}>
              <div className={`text-[10px] uppercase tracking-wider ${isLight ? "text-emerald-200/80" : "text-emerald-950/70"}`}>Balance</div>
              <div className={`font-display text-lg font-semibold ${isLight ? "text-white" : "text-emerald-950"}`}>All settled ✓</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetTheApp;
